import { Controller } from "react-hook-form";
import { TextInput, Select, Checkbox, FormSection } from "../../../../_components/ui";
import TextEditor from "../../../../_components/ui/TextEditor";
import ContactsTable from "../../../proposals/components/ContactsTable";

const UPSELL_BEHAVIOUR_OPTIONS = [
  { label: "Generate prorated upsell invoice", value: "prorated_invoice" },
  { label: "Charge in next cycle as overage", value: "next_cycle_overage" },
];

const EFFECTIVE_DATE_OPTIONS = [
  { label: "Immediately after payment", value: "immediate_payment" },
  { label: "Days after payment", value: "days_after_payment" },
];

/**
 * Step 3 - finalize. Mirrors _features/proposals' FinalizeDetailsStep's
 * shape: `sendInvoice` only renders while `regenerateInvoice` is checked
 * and is force-reset the moment it's unchecked, right where that happens -
 * not a useEffect watching regenerateInvoice.
 *
 * The old wizard used a radio-button group (`FormGroupInputRadioGroup`,
 * old-tree) for `upsellBehaviour`/`effectiveDate` - both are small,
 * mutually-exclusive option sets, so this uses `_components/ui`'s Select
 * instead (same selection outcome, no new old-tree dependency for a
 * pattern the modern component set already covers) rather than pulling in
 * a one-off radio-group primitive for this single wizard.
 */
const UpsellFinalizeStep = ({
  control,
  setValue,
  watch,
  users = [],
  sendTo = [],
  onAddSendToContact,
  onRemoveSendToContact,
  isEdit = false,
  isPreview = false,
}) => {
  const regenerateInvoice = watch("regenerateInvoice");
  const upsellBehaviour = watch("upsellBehaviour");
  const effectiveDate = watch("effectiveDate");
  const type = watch("type") || "Upsell";

  // A raw user record from getUsersByOrgId has no `.name` field of its own
  // (only `.firstName`/`.lastName`) - same fix already applied in
  // Proposals' FinalizeDetailsStep for the identical closedBy picker.
  const userOptions = users.map((user) => ({
    label:
      user.name || `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || "Unnamed user",
    value: user._id,
  }));

  return (
    <div className="upsell-finalize-step">
      <FormSection title="Finalize details">
        <div className="upsell-finalize-step__row">
          <Controller
            name="expiryIn"
            control={control}
            rules={{
              required: "Required.",
              min: { value: 1, message: "Must be at least 1." },
              max: { value: 365, message: "Must be at most 365." },
            }}
            render={({ field, fieldState }) => (
              <TextInput
                id="expiryIn"
                label={`${type} expires in (days)`}
                type="number"
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                disabled={isPreview}
              />
            )}
          />

          <Controller
            name="netTerms"
            control={control}
            rules={{
              min: { value: 0, message: "Must be at least 0." },
              max: { value: 365, message: "Must be at most 365." },
            }}
            render={({ field, fieldState }) => (
              <TextInput
                id="netTerms"
                label="Net terms (days)"
                type="number"
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                disabled={isPreview}
              />
            )}
          />
        </div>

        <Controller
          name="upsellBehaviour"
          control={control}
          render={({ field }) => (
            <Select
              id="upsellBehaviour"
              label={`${type} behaviour`}
              options={UPSELL_BEHAVIOUR_OPTIONS}
              value={field.value}
              onChange={field.onChange}
              disabled={isPreview}
            />
          )}
        />

        {upsellBehaviour === "prorated_invoice" && (
          <div className="upsell-finalize-step__conditional">
            <Controller
              name="effectiveDate"
              control={control}
              render={({ field }) => (
                <Select
                  id="effectiveDate"
                  label="Effective date"
                  options={EFFECTIVE_DATE_OPTIONS}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    if (value !== "days_after_payment") setValue("effectiveDateDays", "");
                    else if (!watch("effectiveDateDays")) setValue("effectiveDateDays", "1");
                  }}
                  disabled={isPreview}
                />
              )}
            />

            {effectiveDate === "days_after_payment" && (
              <Controller
                name="effectiveDateDays"
                control={control}
                rules={{ min: { value: 1, message: "Must be at least 1." } }}
                render={({ field, fieldState }) => (
                  <TextInput
                    id="effectiveDateDays"
                    label="Days after payment"
                    type="number"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                    disabled={isPreview}
                  />
                )}
              />
            )}
          </div>
        )}

        <Controller
          name="requiresCustomerConfirmation"
          control={control}
          render={({ field }) => (
            <Checkbox
              id="requiresCustomerConfirmation"
              label="Requires customer confirmation"
              description={`Should we require customer confirmation for this ${type.toLowerCase()}?`}
              value={field.value}
              onChange={field.onChange}
              disabled={isPreview}
            />
          )}
        />

        <Controller
          name="onInvoice"
          control={control}
          render={({ field }) => (
            <Checkbox
              id="onInvoice"
              label="Display net terms on invoice"
              description="Should we show net terms on invoice?"
              value={field.value}
              onChange={field.onChange}
              disabled={isPreview}
            />
          )}
        />

        <Controller
          name="regenerateInvoice"
          control={control}
          render={({ field }) => (
            <Checkbox
              id="regenerateInvoice"
              label={`${isEdit ? "Regenerate" : "Generate"} invoice`}
              description={`Should we ${isEdit ? "regenerate" : "generate"} invoice right away or after payment?`}
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
                if (!value) setValue("sendInvoice", false);
              }}
              disabled={isPreview}
            />
          )}
        />

        {regenerateInvoice && (
          <Controller
            name="sendInvoice"
            control={control}
            render={({ field }) => (
              <Checkbox
                id="sendInvoice"
                label="Send invoice"
                description="Should we send the invoice to the customer?"
                value={field.value}
                onChange={field.onChange}
                disabled={isPreview}
              />
            )}
          />
        )}

        {isEdit && (
          <Controller
            name="resendProposal"
            control={control}
            render={({ field }) => (
              <Checkbox
                id="resendProposal"
                label="Resend proposal"
                description="Should we resend the proposal?"
                value={field.value}
                onChange={field.onChange}
                disabled={isPreview}
              />
            )}
          />
        )}

        <Controller
          name="closedBy"
          control={control}
          render={({ field }) => (
            <Select
              id="closedBy"
              label={`${type} closed by`}
              options={userOptions}
              value={field.value?._id}
              onChange={(value) => field.onChange(users.find((user) => user._id === value) || null)}
              disabled={isPreview}
            />
          )}
        />

        <Controller
          name="proposalTerms"
          control={control}
          render={({ field }) => (
            <TextEditor
              textEditorValue={field.value}
              setTextEditorValue={field.onChange}
              placeholder="Proposal terms"
              disabled={isPreview}
            />
          )}
        />
      </FormSection>

      <FormSection>
        <ContactsTable
          title={`Send ${type.toLowerCase()} to`}
          contacts={sendTo}
          onAdd={onAddSendToContact}
          onRemove={onRemoveSendToContact}
        />
      </FormSection>
    </div>
  );
};

export default UpsellFinalizeStep;
